import type {
  ContentConfigStatus,
  ProductBodyApi,
} from 'omega-rtc/types/ts/content-config';

import { productToApiBody, resolveGenerationObjectives } from './adapters';

export interface GeneratedContentItem {
  title: string;
  message: string;
  cvp: string;
  score: number;
  reviewResult?: string;
  reviewReason?: string;
}

export interface GenerationContext {
  country: string;
  endDate: string;
  language: string;
  objectiveID?: string;
  objectiveName?: string;
  vertical: string;
  product: string;
  startDate: string;
}

// Which Product Offering configuration a generation run is based on. Anything
// other than 'active' is sent to Mystique inline, because its UCD cache holds
// ACTIVE configs only.
export type GenerationConfigSource = 'active' | 'pending' | 'draft' | 'local-edits';

export interface GenerationProductConfigMeta {
  country: string;
  key: string;
  status: ContentConfigStatus;
}

// Mirror of one UCD /products/list config entry. Sent for a DRAFT or
// PENDING_APPROVAL offering, which Mystique cannot resolve from its
// ACTIVE-only cache.
export interface GenerationProductConfig {
  meta: GenerationProductConfigMeta;
  body: ProductBodyApi;
}

export interface GenerationRequest {
  audienceKey: string;
  payload: {
    countryCode: string;
    endDate: string;
    language: string;
    objectiveID?: string;
    productConfig?: GenerationProductConfig;
    productUUID: string;
    segmentName: string;
    segmentType: 'audience';
    startDate: string;
    vertical: string;
  };
  ucdPayload?: Record<string, any>;
}

const buildMystiqueV2Payload = ({
  audienceKey,
  country,
  endDate,
  language,
  objectiveID,
  productConfig,
  productUUID,
  startDate,
  vertical,
}: {
  audienceKey: string;
  country: string;
  endDate: string;
  language: string;
  objectiveID?: string;
  productConfig?: GenerationProductConfig | null;
  productUUID: string;
  startDate: string;
  vertical: string;
}) => ({
  countryCode: country,
  endDate,
  language,
  ...(objectiveID ? { objectiveID } : {}),
  ...(productConfig ? { productConfig } : {}),
  productUUID,
  segmentName: audienceKey,
  segmentType: 'audience',
  startDate,
  vertical,
});

const buildUcdTestGeneratePayload = ({
  audienceKey,
  baseConfigVersion,
  country,
  endDate,
  language,
  productName,
  productUUID,
  startDate,
  vertical,
}: {
  audienceKey: string;
  baseConfigVersion: number;
  country: string;
  endDate: string;
  language: string;
  productName?: string;
  productUUID: string;
  startDate: string;
  vertical: string;
}) => ({
  request_id: `omega-rtc-content-config-generation-${audienceKey}-${Date.now()}`,
  base_config_version: baseConfigVersion,
  input: {
    channel: 'push',
    country_code: country,
    language,
    vertical,
    product_uuid: productUUID,
    product_offering: productName || productUUID,
    product_name: productName || productUUID,
    content_type: 'Push Notification',
    num: 1,
    use_emoji: true,
    segment_type: 'audience',
    segment_name: audienceKey,
    start_date: startDate,
    end_date: endDate,
  },
});

// Reuses the create/update body adapter so the configuration Mystique receives
// is identical to the one UCD would return once the offering is approved. The
// one departure is objectives: Mystique matches `objectiveID` against
// `body.objectives[].ID`, and productToApiBody omits `ID` for an objective UCD
// has not minted one for, so the resolved generation id is substituted here.
// The key stays absent when the version has no objectives, preserving the
// legacy body.
export const buildGenerationProductConfig = (
  feProduct: any,
  meta: GenerationProductConfigMeta,
): GenerationProductConfig => {
  const body = productToApiBody(feProduct);
  const objectives = resolveGenerationObjectives(
    (feProduct && feProduct.whitelist && feProduct.whitelist.objectives) || [],
  ).map((objective) => ({
    ID: objective.generationID,
    name: (objective.name || '').trim(),
    description: (objective.description || '').trim(),
    source: (objective.source || '').trim(),
  }));

  return {
    meta,
    body: objectives.length ? { ...body, objectives } : body,
  };
};

// A language can be generated once it carries both a name and an overview,
// falling back to English the way Mystique falls back on config_by_lang.
export const resolveGenerationLanguageConfig = (
  productConfig: GenerationProductConfig | null,
  language: string,
) => {
  const configByLang = (productConfig && productConfig.body.config_by_lang) || {};
  const candidate = configByLang[language] || configByLang.en;
  if (!candidate) return null;
  const hasContent = Boolean(
    (candidate.name || '').trim() && (candidate.product_overview || '').trim(),
  );
  return hasContent ? candidate : null;
};

export const buildContentConfigGenerationRequests = ({
  audienceKeys,
  baseConfigVersion = 0,
  country,
  endDate,
  language,
  objectiveID,
  productConfig,
  productName,
  productUUID,
  startDate,
  useUcdContentGeneration = false,
  vertical,
}: {
  audienceKeys: string[];
  baseConfigVersion?: number;
  country: string;
  endDate: string;
  language: string;
  objectiveID?: string;
  productConfig?: GenerationProductConfig | null;
  productName?: string;
  productUUID: string;
  startDate: string;
  useUcdContentGeneration?: boolean;
  vertical: string;
}): GenerationRequest[] => audienceKeys.map((audienceKey) => {
  const payload = buildMystiqueV2Payload({
    audienceKey,
    country,
    endDate,
    language,
    ...(objectiveID ? { objectiveID } : {}),
    ...(productConfig ? { productConfig } : {}),
    productUUID,
    startDate,
    vertical,
  });

  // MarketingContentGenerationInput has no objective field and
  // ContentGenerationTestOverrides.ProductConfig cannot express a UCD product
  // body, so a run needing either would silently lose it on the UCD endpoint.
  const canUseUcdEndpoint = useUcdContentGeneration
    && baseConfigVersion > 0
    && !productConfig
    && !objectiveID;

  return {
    audienceKey,
    payload,
    ...(canUseUcdEndpoint ? {
      ucdPayload: buildUcdTestGeneratePayload({
        audienceKey,
        baseConfigVersion,
        country,
        endDate,
        language,
        productName,
        productUUID,
        startDate,
        vertical,
      }),
    } : {}),
  };
});

const parseMystiqueV2Items = (response: any = {}): GeneratedContentItem[] => {
  if (!response || !Array.isArray(response.payloads)) return [];

  return response.payloads
    .filter((payload) => payload && !payload.error)
    .map((payload) => ({
      title: payload.title || 'Untitled',
      message: payload.message || payload.body || '',
      cvp: payload.cvp || '',
      score: (payload.review && payload.review.score) || 0,
      reviewResult: payload.review && payload.review.result,
      reviewReason: payload.review && payload.review.reason,
    }));
};

export const parseContentConfigGenerationResults = (
  audienceKeys: string[] = [],
  responsesByAudienceKey: Record<string, any> = {},
): Record<string, GeneratedContentItem[]> => {
  const result: Record<string, GeneratedContentItem[]> = {};
  audienceKeys.forEach((audienceKey) => {
    result[audienceKey] = parseMystiqueV2Items(responsesByAudienceKey[audienceKey]);
  });
  return result;
};

const csvCell = (value: any) => {
  const rawValue = String(value === undefined || value === null ? '' : value);
  const safeValue = /^[\s\u0000-\u001F]*[=+\-@]/.test(rawValue) ? `'${rawValue}` : rawValue;
  return `"${safeValue.replace(/"/g, '""')}"`;
};

export const buildContentConfigGenerationCsv = (
  context: GenerationContext,
  audienceKeys: string[],
  itemsByAudienceKey: Record<string, GeneratedContentItem[]>,
) => {
  const rows: any[][] = [[
    'country',
    'language',
    'vertical',
    'product',
    'audience_key',
    'objective_id',
    'objective_name',
    'start_date',
    'end_date',
    'cvp',
    'score',
    'title',
    'message',
  ]];

  audienceKeys.forEach((audienceKey) => {
    (itemsByAudienceKey[audienceKey] || []).forEach((item) => {
      rows.push([
        context.country,
        context.language,
        context.vertical,
        context.product,
        audienceKey,
        context.objectiveID || '',
        context.objectiveName || '',
        context.startDate,
        context.endDate,
        item.cvp,
        item.score,
        item.title,
        item.message,
      ]);
    });
  });

  return `\uFEFF${rows.map((row) => row.map(csvCell).join(',')).join('\n')}`;
};

export const selectMissingContentConfigGenerationRequests = (
  requests: GenerationRequest[] = [],
  readyAudienceKeys: string[] = [],
) => {
  const readyAudienceKeySet = new Set(readyAudienceKeys);
  return requests.filter(({ audienceKey }) => !readyAudienceKeySet.has(audienceKey));
};

export const contentGenerationFilename = (
  product: string,
  audienceKey?: string,
) => {
  const slug = (value: string) => String(value || 'content')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${slug(product)}-${audienceKey ? slug(audienceKey) : 'all-audiences'}-content.csv`;
};

export const downloadContentConfigGenerationCsv = (
  filename: string,
  content: string,
) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};
